// components/OrderPickerDrawer.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerCloseButton,
  Input,
  InputGroup,
  InputLeftElement,
  VStack,
  HStack,
  Text,
  Spinner,
  useToast,
  Box,
  Flex,
  Divider,
  Badge,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { useCognito } from "../utils/useCognito";

const API_GET_ORDERS =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getAllMedicalOrders";

const normalize = (s) => (s || "").toLowerCase();

export default function OrderPickerDrawer({
  isOpen,
  onClose,
  userId,
  isTestMode = false,
  onSelectOrder,
}) {
  const { getIdToken } = useCognito();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!isOpen) return;

    // No user selected – show empty list, UI will explain
    if (!userId) {
      setOrders([]);
      return;
    }

    let mounted = true;

    (async () => {
      setLoading(true);
      try {
        const token = getIdToken();
        const res = await fetch(API_GET_ORDERS, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId, // ✅ let the backend filter if it supports it
            test: isTestMode,
          }),
        });

        const json = await res.json();
        if (!res.ok) {
          throw new Error(json.message || "Failed to load orders");
        }

        // Support multiple shapes:
        // - { ordersByUser: { [userId]: [...] } }
        // - { orders: [...] }
        // - { items: [...] }
        const byUser = json?.ordersByUser;
        let list = [];

        if (byUser && typeof byUser === "object") {
          list = byUser[userId] || [];
        } else if (Array.isArray(json?.orders)) {
          list = json.orders;
        } else if (Array.isArray(json?.items)) {
          list = json.items;
        }

        const sorted = [...list].sort(
          (a, b) =>
            new Date(b.updatedAt || b.createdAt || 0) -
            new Date(a.updatedAt || a.createdAt || 0)
        );

        if (mounted) setOrders(sorted);
      } catch (err) {
        console.error("Order fetch failed:", err);
        toast({
          title: "Failed to load orders",
          description: err.message || "Check the getAllMedicalOrders Lambda.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [isOpen, userId, isTestMode, getIdToken, toast]);

  const filtered = useMemo(() => {
    if (!search) return orders;
    const q = normalize(search);
    return orders.filter((o) =>
      [o.orderId, o.ivrId, o.patientName, o.facilityName, o.status]
        .map((v) => normalize(String(v || "")))
        .some((s) => s.includes(q))
    );
  }, [orders, search]);

  const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

  const renderBodyContent = () => {
    if (!userId) {
      return (
        <Flex flex="1" align="center" justify="center">
          <Text color="gray.500" textAlign="center">
            Pick a user in the invoice form first,
            <br />
            then you can select one of their orders here.
          </Text>
        </Flex>
      );
    }

    if (loading) {
      return (
        <Flex flex="1" align="center" justify="center">
          <Spinner size="lg" />
        </Flex>
      );
    }

    return (
      <Box
        flex="1"
        minH={0}
        maxH="calc(100vh - 220px)"
        overflowY="auto"
        borderRadius="lg"
        borderWidth="1px"
        borderColor="gray.700"
        p={2}
      >
        {filtered.length === 0 ? (
          <Flex align="center" justify="center" h="100%">
            <Text color="gray.500">No orders found for this user.</Text>
          </Flex>
        ) : (
          <VStack align="stretch" spacing={2}>
            {filtered.map((o) => (
              <Box
                key={o.orderId}
                borderRadius="lg"
                borderWidth="1px"
                borderColor="gray.700"
                p={3}
                cursor="pointer"
                _hover={{ bg: "teal.50", borderColor: "teal.400" }}
                transition="background-color 0.12s ease-out, border-color 0.12s ease-out"
                onClick={() => {
                  onSelectOrder?.(o);
                  onClose?.();
                }}
              >
                <HStack justify="space-between" align="flex-start">
                  <VStack align="flex-start" spacing={0}>
                    <Text fontWeight="semibold">Order {o.orderId}</Text>
                    {o.ivrId && (
                      <Text fontSize="xs" color="gray.500">
                        IVR: {o.ivrId}
                      </Text>
                    )}
                    <Text fontSize="sm" color="gray.600">
                      {o.patientName || "Unknown Patient"}
                    </Text>
                    {o.facilityName && (
                      <Text fontSize="xs" color="gray.500">
                        {o.facilityName}
                      </Text>
                    )}
                  </VStack>
                  <VStack align="flex-end" spacing={1}>
                    <Badge colorScheme="purple">
                      {o.status || "—"}
                    </Badge>
                    <Text fontSize="xs" color="gray.500">
                      Updated: {fmtDate(o.updatedAt || o.createdAt)}
                    </Text>
                  </VStack>
                </HStack>
                <Divider my={2} opacity={0.2} />
                <Text fontSize="xs" color="gray.500">
                  Click to use this Order ID.
                </Text>
              </Box>
            ))}
          </VStack>
        )}
      </Box>
    );
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">Select Order</DrawerHeader>
        <DrawerBody pt={4} display="flex" flexDirection="column" gap={4}>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search by order ID, IVR ID, patient, facility, status"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              borderRadius="xl"
            />
          </InputGroup>

          {renderBodyContent()}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
